import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useEffect, useRef, useState } from 'react';
import rehypeRaw from 'rehype-raw';
import Markdown from 'react-markdown';
import { Accordion, AccordionItem, itemState } from '../ui/Accordion';

const FAQElement = ({ question, answer, onToggle }) => {
	const [state, setState] = useState(itemState.Closed);

	const handleToggle = (_, next) => {
		setState(next);
		onToggle && onToggle();
	};

	return (
		<AccordionItem title={question} icon={true} statusBorder={true} state={state} onToggle={handleToggle}>
			<Markdown
				rehypePlugins={[rehypeRaw]}
				components={{
					ul: ({ children }) => <ul className='list-disc'>{children}</ul>,
					ol: ({ children }) => <ol className='list-decimal'>{children}</ol>,
					li: ({ children }) => <li className='ml-4 mb-4'>{children}</li>,
					h5: ({ children }) => <h5 className='text-lg font-medium'>{children}</h5>,
					strong: ({ children }) => <span className='font-medium'>{children}</span>,
					p: ({ children }) => <p className='mb-4 text-md font-normal'>{children}</p>,
				}}
			>
				{answer}
			</Markdown>
		</AccordionItem>
	);
};

const Guide = ({}) => {
	const { t } = useTranslation();
	const [scrollable, setScrollable] = useState(false);
	const containerRef = useRef(null);
	const app_name = t('general.app_name');
	const [loaded, setLoaded] = useState(false);
	const [search, setSearch] = useState('');
	const [accordionToggled, setAccordionToggled] = useState(0);
	const content = t('guide_page.faq', {
		returnObjects: true,
		app_name,
	});
	const [filteredContent, setFilteredContent] = useState(content);

	useEffect(() => {
		if (typeof content === 'object' && content.length > 0) {
			setLoaded(true);
		}
	}, [content]);

	useEffect(() => {
		const container = containerRef.current;
		const windowHeight = window.innerHeight;

		// verify content height larger than container height + preceding elements height
		if (container && container.scrollHeight + 280 > windowHeight) {
			setScrollable(true);
		} else if (container) {
			setScrollable(false);
		}
	}, [accordionToggled]);

	const handleScrollToBottom = () => {
		const container = containerRef.current;

		// scroll entire content to bottom
		window.scrollTo({
			top: container.scrollHeight,
			behavior: 'smooth',
		});
	};

	useEffect(() => {
		if (search !== '') {
			setFilteredContent(content.filter((faq) => faq.question.toLowerCase().includes(search.toLowerCase())));
		}
	}, [search]);

	const handleAccordionToggle = () => {
		setAccordionToggled((prev) => prev + 1);
	};

	return (
		<div className='w-2xl' ref={containerRef}>
			<div className='flex flex-col gap-6'>
				<div className='flex flex-col gap-4'></div>
				<Input
					type='text'
					placeholder={t('guide_page.search')}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>

				<div className='flex flex-col mb-20'>
					<div className='flex flex-col gap-8 pb-5'>
						<div className='font-base leading-[1.4] tracking-wide'>
							{loaded && (
								<Accordion>
									{search !== '' && typeof filteredContent === 'object' && filteredContent.length > 0
										? filteredContent.map((faq) => (
												<FAQElement
													key={faq.question}
													question={faq.question}
													answer={faq.answer}
													onToggle={handleAccordionToggle}
												/>
										  ))
										: content.map((faq) => (
												<FAQElement
													key={faq.question}
													question={faq.question}
													answer={faq.answer}
													onToggle={handleAccordionToggle}
												/>
										  ))}
								</Accordion>
							)}
						</div>
					</div>
				</div>
				<div className='fixed bottom-0 w-full h-28 bg-gradient-to-t from-white to-transparent pointer-events-none' />
			</div>

			{scrollable && (
				<div className='fixed bottom-4 right-4'>
					<Button onClick={handleScrollToBottom} size='medium' variant='primary' type='solid'>
						<i className='ri-arrow-down-line text-sm leading-[1.4]' />
					</Button>
				</div>
			)}
		</div>
	);
};

Guide.propTypes = {};

export default Guide;
